import {
	Autocomplete,
	Box,
	Button,
	Divider,
	IconButton,
	InputAdornment,
	InputBase,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import queryString from 'query-string';
import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { COLORS } from '../../../services/constants/colors';
import { ETableStructureType } from 'ui/EasTable/types';
import { YearMonthPiker } from 'ui/YearMonthPiker';
import { TSeparateFilterProps } from './types';

/** The `YYYY-MM` a picked year and month are stored as. */
const PERIOD_PATTERN = /^(\d{4})-(\d{2})$/;

type TOption = {
	value: string;
	label: string;
};

export const SeparateFilter = ({
	name,
	selector,
	type,
	filter,
	defaultValue,
	autocomplete = false,
}: TSeparateFilterProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const [changed, setChanged] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [debouncedValue] = useDebounce(inputValue, 1000);
	const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
	const [dateMonthMenuAnchor, setDateMonthMenuAnchor] = useState<null | HTMLElement>(null);

	const isStatusType =
		!autocomplete &&
		(type === ETableStructureType.STATUS || type === ETableStructureType.STATUS_NO_BG);
	const isDateMonthType = !autocomplete && type === ETableStructureType.DATE_MONTH;
	const statusOptions = filter ?? [];
	/** Every variant but the plain text field picks its value instead of typing it. */
	const isPickerType = autocomplete || isStatusType || isDateMonthType;

	const paramSelector = `f-${selector}`;
	const paramValue = navigateParams[paramSelector];
	/**
	 * The default applies only while the url says nothing about this filter. Clearing a defaulted
	 * filter leaves an empty param behind, so it stays cleared instead of falling back again.
	 */
	const clearedValue = defaultValue !== undefined ? '' : undefined;
	const paramValueStr =
		paramValue === undefined || paramValue === null
			? (defaultValue ?? '')
			: paramValue === ''
				? ''
				: String(paramValue);

	const selectedStatusLabel = statusOptions.find((o) => String(o[0]) === paramValueStr)?.[1] ?? '';

	useEffect(() => {
		if (isPickerType) return;
		if (changed) {
			navigate({
				pathname: location.pathname,
				search: `?${queryString.stringify({
					...navigateParams,
					[`f-${selector}`]: debouncedValue || clearedValue,
					page: 1,
				})}`,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	useEffect(() => {
		if (isPickerType) return;
		setInputValue(paramValueStr);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	const navigateWithParam = (value: string | undefined) => {
		navigate({
			pathname: location.pathname,
			search: `?${queryString.stringify({
				...navigateParams,
				[`f-${selector}`]: value,
				page: 1,
			})}`,
		});
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setChanged(true);
		setInputValue(e.target?.value ?? '');
	};

	const handleClear = () => {
		setChanged(true);
		setInputValue('');
		if (isPickerType) {
			navigateWithParam(clearedValue);
		}
	};

	const autocompleteOptions = useMemo<TOption[]>(
		() => (filter ?? []).map((item) => ({ value: String(item[0]), label: String(item[1]) })),
		[filter],
	);

	/**
	 * The options can still be loading while the url already carries a value, so an unknown one is
	 * shown as typed instead of leaving the field empty next to a table that is filtered by it.
	 */
	const autocompleteValue = useMemo<TOption | null>(() => {
		if (!paramValueStr) return null;
		return (
			autocompleteOptions.find((option) => option.value === paramValueStr) ?? {
				value: paramValueStr,
				label: paramValueStr,
			}
		);
	}, [autocompleteOptions, paramValueStr]);

	const period = PERIOD_PATTERN.exec(paramValueStr);
	const periodLabel = period ? DateTime.fromISO(paramValueStr).toFormat('MM.yyyy') : '';

	/**
	 * Navigating here rather than letting the picker write the param itself keeps the value under the
	 * `f-` prefix the separate filters are read from, and resets the paging like the other variants.
	 */
	const handlePeriodSelect = ({ year, month }: { year: number; month: number }) => {
		navigateWithParam(`${year}-${String(month).padStart(2, '0')}`);
		setDateMonthMenuAnchor(null);
	};

	const handleStatusSelect = (optionValue: string | number | null) => {
		const serialized =
			optionValue === null ? 'null' : optionValue === '' ? clearedValue : String(optionValue);
		navigateWithParam(serialized);
		setStatusMenuAnchor(null);
	};

	return (
		<Box
			component="div"
			sx={{
				display: 'flex',
				alignItems: 'center',
				borderRadius: '12px',
				borderWidth: '1px',
				borderStyle: 'solid',
				borderColor: 'primary.main',
				fontSize: '.9rem',
				p: 0,
				m: 0,
			}}
		>
			{autocomplete && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '1px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
								whiteSpace: 'nowrap',
							}}
						>
							{name}:
						</Typography>
					</Box>
					<Autocomplete<TOption>
						options={autocompleteOptions}
						value={autocompleteValue}
						onChange={(event, option) => navigateWithParam(option?.value ?? clearedValue)}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, selected) => option.value === selected.value}
						clearIcon={<Close sx={{ fontSize: '14px' }} />}
						forcePopupIcon={false}
						openOnFocus
						size="small"
						// The clear icon is positioned against the input, so it is nudged to the box edge.
						sx={{ minWidth: '110px', '& .MuiAutocomplete-endAdornment': { right: '5px' } }}
						componentsProps={{ paper: { sx: { borderRadius: '12px', minWidth: '160px' } } }}
						renderOption={(optionProps, option) => (
							<Box
								component="li"
								{...optionProps}
								key={option.value}
								sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '.9rem' }}
							>
								{option.label}
							</Box>
						)}
						renderInput={(params) => (
							<InputBase
								{...params.InputProps}
								inputProps={params.inputProps}
								placeholder={`Select ${name}`}
								sx={{
									// Fills the field so the clear icon lands on its right edge, with room kept for it.
									width: '100%',
									fontSize: '.9rem',
									p: '0 26px 0 0',
									'& input': {
										padding: '4px 0px',
										fontSize: '.9rem',
										fontWeight: 'bold',
										color: 'primary.main',
										'&::placeholder': {
											fontSize: '.8rem',
											fontWeight: 'normal',
										},
									},
								}}
							/>
						)}
					/>
				</>
			)}
			{!isPickerType && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '2px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
							}}
						>
							{name}:
						</Typography>
					</Box>
					<InputBase
						sx={{
							flex: 1,
							fontSize: '.9rem',
							p: '2px 0px',
							'& input': {
								padding: '4px 0px',
								fontSize: '.9rem',
								'&::placeholder': {
									fontSize: '.8rem',
								},
							},
						}}
						style={{ padding: 0 }}
						size="small"
						placeholder={`Enter ${name}`}
						value={inputValue}
						onChange={handleChange}
						// endAdornment={
						// 	inputValue ? (
						// 		<InputAdornment position="end">
						// 			<IconButton size="small" onClick={handleClear} sx={{ p: 0 }}>
						// 				<Close fontSize="small" />
						// 			</IconButton>
						// 		</InputAdornment>
						// 	) : null
						// }
					/>
					<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
					<IconButton
						color="primary"
						sx={{ p: '0 5px 0 0' }}
						aria-label="Clear filter"
						onClick={handleClear}
					>
						<Close fontSize="small" />
					</IconButton>
				</>
			)}
			{isDateMonthType && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '1px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
								whiteSpace: 'nowrap',
							}}
						>
							{name}:
						</Typography>
					</Box>
					<Button
						id="separate-date-month-filter"
						variant="text"
						size="small"
						onClick={(e) => setDateMonthMenuAnchor(e.currentTarget)}
						endIcon={<ExpandMoreIcon fontSize="small" />}
						sx={{
							flex: 1,
							textTransform: 'none',
							color: 'primary.main',
							fontWeight: 'bold',
							fontSize: '.9rem',
							justifyContent: 'space-between',
							whiteSpace: 'nowrap',
							p: 0,
							m: 0,
							minWidth: 0,
						}}
					>
						{periodLabel || `Select ${name}`}
					</Button>
					<Menu
						anchorEl={dateMonthMenuAnchor}
						open={Boolean(dateMonthMenuAnchor)}
						onClose={() => setDateMonthMenuAnchor(null)}
						MenuListProps={{ 'aria-labelledby': 'separate-date-month-filter' }}
						sx={{ '& .MuiMenu-paper': { borderRadius: '12px' } }}
					>
						<YearMonthPiker
							initialYear={period ? Number(period[1]) : null}
							initialMonth={period ? Number(period[2]) : null}
							onChange={handlePeriodSelect}
							onClose={() => setDateMonthMenuAnchor(null)}
						/>
					</Menu>
					<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
					<IconButton
						color="primary"
						sx={{ p: '0 5px 0 0', m: 0 }}
						aria-label="Clear filter"
						onClick={handleClear}
						disabled={!paramValueStr}
					>
						<Close fontSize="small" sx={{ fontSize: '14px' }} />
					</IconButton>
				</>
			)}
			{isStatusType && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '1px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
							}}
						>
							{name}:
						</Typography>
					</Box>
					<Button
						id="separate-status-filter"
						variant="text"
						size="small"
						onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
						endIcon={<ExpandMoreIcon fontSize="small" />}
						sx={{
							flex: 1,
							textTransform: 'none',
							color: 'primary.main',
							fontWeight: 'bold',
							fontSize: '.9rem',
							justifyContent: 'space-between',
							p: 0,
							m: 0,
							minWidth: 0,
						}}
					>
						{selectedStatusLabel || `Select ${name}`}
					</Button>
					<Menu
						anchorEl={statusMenuAnchor}
						open={Boolean(statusMenuAnchor)}
						onClose={() => setStatusMenuAnchor(null)}
						MenuListProps={{ 'aria-labelledby': 'separate-status-filter' }}
						sx={{
							'& .MuiMenu-paper': { borderRadius: '12px' },
						}}
					>
						{statusOptions.map((optionItem) => (
							<MenuItem
								key={String(optionItem[0])}
								onClick={() => handleStatusSelect(optionItem[0])}
								selected={String(optionItem[0]) === paramValueStr}
								sx={{ color: 'primary.main', fontWeight: 'bold' }}
							>
								{optionItem[1]}
							</MenuItem>
						))}
					</Menu>
					<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
					<IconButton
						color="primary"
						sx={{ p: '0 5px 0 0', m: 0 }}
						aria-label="Clear filter"
						onClick={handleClear}
						disabled={!paramValueStr}
					>
						<Close fontSize="small" sx={{ fontSize: '14px' }} />
					</IconButton>
				</>
			)}
		</Box>
	);
};
